import { FC, useLayoutEffect, useRef, useState } from 'react';

export interface FileInputProps {
    name?: string;
    disabled?: boolean;
    error?: boolean;
    size?: 'small' | 'medium' | 'large';
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    extendedClassName?: string;
    testId: string;
    id?: string;
    fileName?: string;
    accept?: string;
}

const getFileNameBoxWidth = (inputWidth: number, size: FileInputProps['size']) => {
    if (size === 'small') {
        return inputWidth - 100 - 16;
    } else if (size === 'medium') {
        return inputWidth - 115 - 16;
    } else if (size === 'large') {
        return inputWidth - 135 - 16;
    }
};

const middleTruncate = (str: string, size: FileInputProps['size'], inputWidth: number) => {
    const fileNameBoxWidth = getFileNameBoxWidth(inputWidth, size);
    const maxLength = fileNameBoxWidth ? Math.floor(fileNameBoxWidth / 8) : 0;

    if (str.length <= maxLength) return str;
    const keep = Math.floor((maxLength - 3) / 2);
    return str.slice(0, keep) + '...' + str.slice(str.length - keep);
};

const FileInput: FC<FileInputProps> = (props) => {
    const {
        name = '',
        disabled = false,
        extendedClassName = '',
        error = false,
        size = 'medium',
        fileName,
        accept,
    } = props;

    const inputRef = useRef<HTMLInputElement>(null);
    const [inputWidth, setInputWidth] = useState<number>(0);

    useLayoutEffect(() => {
        if (inputRef.current) {
            setInputWidth(inputRef.current.clientWidth);
        }
    }, []);

    const defaultClassName =
        'file-input text-[100%] bg-white border rounded text-gray-900 block font-normal leading-6 bg-clip-padding focus:outline-none focus:border-[#86b7fe] focus:shadow-[inset_0_1px_1px_rgba(0,0,0,0.08),0_0_8px_rgba(102,175,233,0.6)]';

    // Sizes
    let sizeClassName = 'file-input-sm';
    let fileNameBoxClassName =
        'flex items-center absolute bg-white overflow-hidden pointer-events-none whitespace-nowrap h-5';
    const fileNameBoxStyle: React.CSSProperties = {};
    const fileNameBoxWidth = getFileNameBoxWidth(inputWidth, size);
    fileNameBoxStyle.width = `${fileNameBoxWidth}px`;
    if (size === 'small') {
        fileNameBoxClassName += ` left-[116px] top-[6px]`;
    } else if (size === 'medium') {
        sizeClassName = 'file-input-md';
        fileNameBoxClassName += ` left-[128px] top-[10px]`;
    } else if (size === 'large') {
        sizeClassName = 'file-input-lg';
        fileNameBoxClassName += ` left-[151px] top-[14px]`;
    }

    if (disabled) {
        fileNameBoxStyle.backgroundColor = 'var(--color-base-200)';
        fileNameBoxStyle.color = 'color-mix(in oklch, var(--color-base-content) 20%, #0000)';
    }

    const errorClassNames = error
        ? 'file-input-error focus:border-red-500 focus:shadow-[inset_0_1px_1px_rgba(0,0,0,0.08),0_0_8px_rgba(255,99,71,0.6)]'
        : '';

    const disabledClass = disabled
        ? 'disabled:!border-custom-gray-06 disabled:bg-custom-gray-07 disabled:text-custom-black-01 disabled:opacity-100'
        : '';

    const inputFieldClassNames = [
        defaultClassName,
        extendedClassName,
        errorClassNames,
        disabledClass,
        sizeClassName,
    ].join(' ');

    return (
        <div className="w-full relative">
            <input
                type="file"
                className={inputFieldClassNames}
                onChange={props.onChange}
                data-test-id={props.testId}
                id={props.id}
                name={name}
                disabled={disabled}
                aria-invalid={error}
                ref={inputRef}
                accept={accept}
            />
            <div className={fileNameBoxClassName} style={fileNameBoxStyle}>
                {fileName ? middleTruncate(fileName, size, inputWidth) : 'No file chosen'}
            </div>
        </div>
    );
};

export default FileInput;
